import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import useSEO from '../../hooks/useSEO';
import { ManagedOrder, OFFER_MEALS, formatPrice, offersApi, scheduleDays, titleCase } from '../../services/offersApi';
import { OfferShell, OfferState } from './OfferShell';

export default function OfferManagePage() {
  const { orderId = '' } = useParams();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [order, setOrder] = useState<ManagedOrder | null>(null);
  const [loadError, setLoadError] = useState<string | null>(token ? null : 'This link is missing its access code.');
  const [address, setAddress] = useState('');
  const [hotel, setHotel] = useState('');
  const [room, setRoom] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useSEO({ title: 'Manage your reservation - BOQBOX', description: 'View your meal reservation and update delivery details.' });

  useEffect(() => {
    if (!token) return;
    offersApi
      .getManagedOrder(orderId, token)
      .then((data) => {
        setOrder(data);
        setAddress(data.customer_address || '');
        setHotel(data.lodging?.hotel_name || '');
        setRoom(data.lodging?.room_number || '');
      })
      .catch(() => setLoadError('This reservation link is invalid or has expired.'));
  }, [orderId, token]);

  if (loadError) {
    return (
      <OfferShell>
        <OfferState icon="🔒" title="We couldn’t open this reservation" message={`${loadError} Use the link in your confirmation email.`} action={<Link className="mo-btn mo-btn-primary" to="/offers">See offers</Link>} />
      </OfferShell>
    );
  }
  if (!order) {
    return (
      <OfferShell>
        <div className="mo-container mo-section" style={{ display: 'grid', gap: 14 }}>
          {[0, 1].map((i) => <div key={i} className="mo-skeleton" style={{ height: 160 }} />)}
        </div>
      </OfferShell>
    );
  }

  const days = scheduleDays(order.schedule);
  const modeOf = (day: string, meal: string) => {
    const entry = (order.daily_fulfilment || []).find((d) => d.day === day) as Record<string, any> | undefined;
    return entry?.[meal]?.mode;
  };
  const delivers = days.some((day) => OFFER_MEALS.some((meal) => (order.schedule[day]?.[meal] || []).length && modeOf(day, meal) !== 'stand_pickup'));
  const deadline = order.campaign?.hotelUpdateDeadline ? new Date(order.campaign.hotelUpdateDeadline) : null;
  const locked = Boolean(deadline && !Number.isNaN(deadline.getTime()) && Date.now() > deadline.getTime());
  const offerUrl = order.campaign?.slug ? `/offers/${encodeURIComponent(order.campaign.slug)}` : '/offers';

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaveError(null);
    setSaved(false);
    if (delivers && address.trim().length < 6) {
      setSaveError('Enter the address we should deliver to.');
      return;
    }
    setSaving(true);
    try {
      await offersApi.updateDeliveryDetails(orderId, token, {
        customer_address: address.trim(),
        lodging: { status: hotel.trim() ? 'confirmed' : 'pending', hotel_name: hotel.trim(), room_number: room.trim() },
      });
      setSaved(true);
    } catch (e) {
      setSaveError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <OfferShell backTo={offerUrl} backLabel="View offer">
      <div className="mo-container">
        <div className="mo-order-head">
          <h1>Your reservation</h1>
          <p>{order.campaign?.name} · {titleCase(order.plan_name || 'Custom')} plan</p>
        </div>

        <div className="mo-layout">
          <div>
            <form className="mo-card mo-panel" onSubmit={save}>
              <h2>Delivery details</h2>
              <p>{delivers ? 'Tell us where to bring your meals. You can update this any time before the deadline.' : 'All your meals are for pickup — no address needed.'}</p>
              {order.campaign?.pickupInstruction && !delivers ? <div className="mo-note mo-note-info" style={{ marginBottom: 14 }}>📍 {order.campaign.pickupInstruction}</div> : null}
              {locked ? <div className="mo-note" style={{ marginBottom: 14 }}>The deadline for changes has passed. {order.campaign?.supportPhone ? `Call ${order.campaign.supportPhone} if something is wrong.` : ''}</div> : null}
              <div className="mo-form mo-form-2">
                <div className="mo-field mo-span-2">
                  <label htmlFor="mo-address">Delivery address</label>
                  <textarea id="mo-address" rows={2} disabled={locked} value={address} onChange={(e) => { setAddress(e.target.value); setSaved(false); }} placeholder="Street, area, city" />
                </div>
                <div className="mo-field">
                  <label htmlFor="mo-hotel">Hotel or building (optional)</label>
                  <input id="mo-hotel" disabled={locked} value={hotel} onChange={(e) => { setHotel(e.target.value); setSaved(false); }} />
                </div>
                <div className="mo-field">
                  <label htmlFor="mo-room">Room / flat (optional)</label>
                  <input id="mo-room" disabled={locked} value={room} onChange={(e) => { setRoom(e.target.value); setSaved(false); }} />
                </div>
              </div>
              {saveError ? <div className="mo-error" style={{ marginTop: 14 }}>{saveError}</div> : null}
              {saved ? <div className="mo-note mo-note-info" style={{ marginTop: 14, color: 'var(--mo-green)', fontWeight: 700 }}>✓ Saved. We’ll deliver to this address.</div> : null}
              {!locked ? (
                <button className="mo-btn mo-btn-primary" type="submit" disabled={saving} style={{ marginTop: 16 }}>
                  {saving ? <span className="mo-spinner" /> : null}{saving ? 'Saving…' : 'Save details'}
                </button>
              ) : null}
            </form>

            <div className="mo-card mo-panel">
              <h2>Your meals</h2>
              <p>{days.length} day{days.length === 1 ? '' : 's'}</p>
              {days.map((day) => (
                <div key={day} style={{ padding: '10px 0', borderTop: '1px solid var(--mo-line)' }}>
                  <strong style={{ textTransform: 'capitalize' }}>{day}</strong>
                  {OFFER_MEALS.filter((meal) => (order.schedule[day]?.[meal] || []).length).map((meal) => (
                    <div key={meal} className="mo-sum-row" style={{ marginTop: 6 }}>
                      <span style={{ textTransform: 'capitalize' }}>{meal} · {modeOf(day, meal) === 'stand_pickup' ? 'Pickup' : 'Delivery'}</span>
                      <strong style={{ textAlign: 'right' }}>{(order.schedule[day]?.[meal] || []).map((s) => `${s.itemName}${s.quantity > 1 ? ` ×${s.quantity}` : ''}`).join(', ')}</strong>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <aside className="mo-card mo-summary">
            <h3>Booking</h3>
            <div className="mo-sum-row"><span>Name</span><strong>{order.customer_name}</strong></div>
            <div className="mo-sum-row"><span>Email</span><strong style={{ wordBreak: 'break-all', textAlign: 'right' }}>{order.customer_email}</strong></div>
            {order.total_amount_paid !== undefined ? <div className="mo-sum-row"><span>Paid</span><strong>{formatPrice(order.total_amount_paid)}</strong></div> : null}
            {order.payment_reference ? <div className="mo-sum-row"><span>Reference</span><strong style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>{order.payment_reference}</strong></div> : null}
            {order.campaign?.supportPhone ? <div className="mo-note mo-note-info">Need help? Call {order.campaign.supportPhone}.</div> : null}
          </aside>
        </div>
      </div>
    </OfferShell>
  );
}
